import { MENTION_PATTERN, ROLE_MENTION_PATTERN } from '@nexo/types';

interface Person {
  id: string;
  displayName: string;
}

interface Role {
  id: string;
  name: string;
}

/** `<@id>` / `<@&roleId>` → `@Nombre` for editing in a plain textarea. */
export function toEditable(body: string, people: Person[], roles: Role[] = []): string {
  const byId = new Map(people.map((p) => [p.id, p.displayName]));
  const roleById = new Map(roles.map((r) => [r.id, r.name]));
  return body
    .replace(ROLE_MENTION_PATTERN, (match, id: string) => {
      const name = roleById.get(id);
      return name ? `@${name}` : match;
    })
    .replace(MENTION_PATTERN, (match, id: string) => {
      const name = byId.get(id);
      return name ? `@${name}` : match;
    });
}

/**
 * `@Nombre` → `<@id>` (people) or `<@&id>` (roles) before sending. Longest names first so
 * "Ana García" wins over "Ana"; on equal names a person wins over a role.
 */
export function fromEditable(text: string, people: Person[], roles: Role[] = []): string {
  const targets = [
    ...people.map((p) => ({ name: p.displayName, token: `<@${p.id}>`, person: true })),
    ...roles.map((r) => ({ name: r.name, token: `<@&${r.id}>`, person: false })),
  ].sort((a, b) => b.name.length - a.name.length || Number(b.person) - Number(a.person));
  let result = text;
  for (const target of targets) {
    const escaped = target.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(
      new RegExp(`(^|[\\s(])@${escaped}(?![\\p{L}\\p{N}_])`, 'gu'),
      `$1${target.token}`,
    );
  }
  return result;
}

/** Active `@query` or `/command` token right before the caret, if any. */
export function tokenAtCaret(
  text: string,
  caret: number,
): { kind: '@' | '/'; query: string; start: number } | null {
  const before = text.slice(0, caret);
  const mention = /(^|\s)@([\p{L}\p{N}_.-]*)$/u.exec(before);
  if (mention)
    return { kind: '@', query: mention[2] ?? '', start: caret - (mention[2]?.length ?? 0) - 1 };
  const command = /^\/([a-z]*)$/.exec(before);
  if (command) return { kind: '/', query: command[1] ?? '', start: 0 };
  return null;
}

const WORD_CHAR = /[\p{L}\p{N}_]/u;

/**
 * Wraps the selection with markdown markers. With nothing selected, the word under the caret is
 * wrapped; between words the markers are inserted around a placeholder (empty: caret inside).
 */
export function wrapSelection(
  text: string,
  start: number,
  end: number,
  marker: string,
  closing = marker,
  placeholder = 'texto',
): { text: string; selectionStart: number; selectionEnd: number } {
  if (start === end) {
    while (start > 0 && WORD_CHAR.test(text[start - 1] ?? '')) start--;
    while (end < text.length && WORD_CHAR.test(text[end] ?? '')) end++;
  }
  const selected = text.slice(start, end) || placeholder;
  const next = text.slice(0, start) + marker + selected + closing + text.slice(end);
  return {
    text: next,
    selectionStart: start + marker.length,
    selectionEnd: start + marker.length + selected.length,
  };
}
